/**
 * Provider Subscription Table
 * Displays Stripe Products and Subscriptions with management actions
 */

import React, { useState, useEffect } from 'react';
import {
  Paper,
  Chip,
  Alert,
  Stack,
  Typography,
  Tooltip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import { AppButton, AppIconButton } from '../design-system';
import {
  Visibility as VisibilityIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenInNewIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import type { StripeProductResponse } from '@cnapse/shared/schemas.js';
import { providerStripeService } from '../../services/ProviderStripeService';
import { getFeatureLabel } from '@cnapse/shared';
import { EnhancedTable, type TableColumn, type TableAction } from '../ui/EnhancedTable';
import { JsonEditor } from '../ui/JsonEditor';
import { jsonStringify } from '../../utils/jsonFormat';

interface ProviderSubscriptionTableProps {
  refreshTrigger?: number;
  onAdd?: () => void;
  addLabel?: string;
}

interface StripePrice {
  id: string;
  active: boolean;
  currency: string;
  unit_amount: number | null;
  recurring: {
    interval: string;
    usage_type?: string;
  } | null;
  metadata: Record<string, string>;
}

const ProviderSubscriptionTable: React.FC<ProviderSubscriptionTableProps> = ({
  refreshTrigger,
  onAdd,
  addLabel,
}) => {
  const [products, setProducts] = useState<StripeProductResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<StripeProductResponse | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [filters, setFilters] = useState<Record<string, unknown>>({});

  const loadProducts = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await providerStripeService.listStripeProducts({ active: true, limit: 100 });
      setProducts(response.data.products);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Stripe Products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, [refreshTrigger]);

  const handleCopyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
    console.log(`Copied ${label}: ${text}`);
  };

  const handleViewDetails = (product: StripeProductResponse) => {
    setSelectedProduct(product);
    setDetailsOpen(true);
  };

  const handleArchive = async (productId: string, productName: string) => {
    if (!window.confirm(`Archive product "${productName}"?\n\nThis will deactivate it in Stripe.`)) {
      return;
    }

    try {
      await providerStripeService.archiveStripeProduct(productId);
      await loadProducts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive product');
    }
  };

  const formatCurrency = (cents: number | null): string => {
    if (cents === null || cents === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const getBasePrice = (product: StripeProductResponse) => {
    const basePrice = product.prices.find((p: StripePrice) => p.metadata.resource_type === 'base');
    return basePrice ? formatCurrency(basePrice.unit_amount) : 'N/A';
  };

  const getOveragePrices = (product: StripeProductResponse) => {
    const overageTypes = ['wafers', 'containers', 'users', 'axon_gateways', 'storage'];
    return product.prices.filter(
      (p: StripePrice) => p.metadata.resource_type && overageTypes.includes(p.metadata.resource_type)
    );
  };

  const getFeaturePrices = (product: StripeProductResponse) => {
    return product.prices.filter(
      (p: StripePrice) => p.metadata.resource_type === 'feature'
    );
  };

  const getEncryptionPrice = (product: StripeProductResponse) => {
    const encryptionPrice = product.prices.find((p: StripePrice) => p.metadata.resource_type === 'encryption');
    return encryptionPrice;
  };

  const getTotalFixedCost = (product: StripeProductResponse) => {
    const basePrice = product.prices.find((p: StripePrice) => p.metadata.resource_type === 'base');
    const baseCost = basePrice?.unit_amount || 0;
    
    const encryptionCost = getEncryptionPrice(product)?.unit_amount || 0;
    
    const featuresCost = getFeaturePrices(product).reduce(
      (sum, price) => sum + (price.unit_amount || 0),
      0
    );
    
    return baseCost + encryptionCost + featuresCost;
  };

  const filteredProducts = products.filter((product) => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = 
        product.name.toLowerCase().includes(search) ||
        product.id.toLowerCase().includes(search) ||
        (product.description && product.description.toLowerCase().includes(search));
      if (!matchesSearch) return false;
    }

    // Status filter
    if (filters.status) {
      if (filters.status === 'active' && !product.active) return false;
      if (filters.status === 'archived' && product.active) return false;
    }

    return true;
  });

  // Export handler
  const handleExport = () => {
    const csvHeaders = ['Product Name', 'Product ID', 'Base Price', 'Total Fixed Cost', 'Status'];
    const csvRows = filteredProducts.map((product) => [
      `"${product.name}"`,
      product.id,
      getBasePrice(product),
      formatCurrency(getTotalFixedCost(product)),
      product.active ? 'Active' : 'Archived',
    ]);

    const csvContent = [
      csvHeaders.join(','),
      ...csvRows.map(row => row.join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `stripe-products-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Define table columns
  const columns: TableColumn<StripeProductResponse>[] = [
    {
      field: 'name',
      headerName: 'Product Name',
      flex: 2,
      minWidth: 200,
      sortable: true,
      renderCell: (row) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {row.name}
          </Typography>
          {row.description && (
            <Typography variant="caption" color="text.secondary" display="block">
              {row.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'id',
      headerName: 'Product ID',
      flex: 1.5,
      minWidth: 180,
      sortable: false,
      renderCell: (row) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
            {row.id.substring(0, 20)}...
          </Typography>
          <Tooltip title="Copy Product ID">
            <AppIconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleCopyToClipboard(row.id, 'Product ID');
              }}
            >
              <CopyIcon fontSize="small" />
            </AppIconButton>
          </Tooltip>
        </Stack>
      ),
    },
    {
      field: 'basePrice',
      headerName: 'Base Price',
      width: 120,
      align: 'center',
      sortable: true,
      valueGetter: (row) => {
        const price = getBasePrice(row);
        return price === 'N/A' ? 0 : parseFloat(price.replace(/[$,]/g, ''));
      },
      renderCell: (row) => (
        <Typography variant="body2" fontWeight="medium">
          {getBasePrice(row)}
        </Typography>
      ),
    },
    {
      field: 'features',
      headerName: 'Features & Encryption',
      flex: 2,
      minWidth: 220,
      sortable: false,
      align: 'center',
      renderCell: (row) => {
        const featurePrices = getFeaturePrices(row);
        const encryptionPrice = getEncryptionPrice(row);
        
        // Build tooltip content with all items
        const tooltipContent = (
          <Box sx={{ p: 0.5 }}>
            {featurePrices.map((price: StripePrice) => (
              <Typography key={price.id} variant="caption" display="block">
                {getFeatureLabel(price.metadata.feature_name)}: {formatCurrency(price.unit_amount)}
              </Typography>
            ))}
            {encryptionPrice && (
              <Typography variant="caption" display="block">
                encryption: {formatCurrency(encryptionPrice.unit_amount)}
              </Typography>
            )}
          </Box>
        );
        
        // Show first feature if available, otherwise encryption, otherwise None
        if (featurePrices.length > 0) {
          const firstFeature = featurePrices[0];
          const remainingCount = featurePrices.length + (encryptionPrice ? 1 : 0) - 1;
          return (
            <Tooltip title={tooltipContent} arrow>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Chip
                  label={`${getFeatureLabel(firstFeature.metadata.feature_name)}: ${formatCurrency(firstFeature.unit_amount)}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
                {remainingCount > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    +{remainingCount}
                  </Typography>
                )}
              </Stack>
            </Tooltip>
          );
        } else if (encryptionPrice) {
          return (
            <Chip
              label={`encryption: ${formatCurrency(encryptionPrice.unit_amount)}`}
              size="small"
              color="success"
              variant="outlined"
            />
          );
        } else {
          return (
            <Typography variant="caption" color="text.secondary">
              None
            </Typography>
          );
        }
      },
    },
    {
      field: 'totalFixedCost',
      headerName: 'Total Fixed Cost',
      width: 140,
      align: 'center',
      sortable: true,
      valueGetter: (row) => getTotalFixedCost(row),
      renderCell: (row) => (
        <Typography variant="body2" fontWeight="bold" color="primary.main">
          {formatCurrency(getTotalFixedCost(row))}
        </Typography>
      ),
    },
    {
      field: 'overage',
      headerName: 'Overage Pricing',
      flex: 2,
      minWidth: 220,
      sortable: false,
      align: 'center',
      renderCell: (row) => {
        const overagePrices = getOveragePrices(row);
        
        // Build tooltip content with all items
        const tooltipContent = (
          <Box sx={{ p: 0.5 }}>
            {overagePrices.map((price: StripePrice) => (
              <Typography key={price.id} variant="caption" display="block">
                {price.metadata.resource_type}: {formatCurrency(price.unit_amount)}
              </Typography>
            ))}
          </Box>
        );
        
        if (overagePrices.length > 0) {
          const firstOverage = overagePrices[0];
          const remainingCount = overagePrices.length - 1;
          return (
            <Tooltip title={remainingCount > 0 ? tooltipContent : ''} arrow>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Chip
                  label={`${firstOverage.metadata.resource_type}: ${formatCurrency(firstOverage.unit_amount)}`}
                  size="small"
                  variant="outlined"
                />
                {remainingCount > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    +{remainingCount}
                  </Typography>
                )}
              </Stack>
            </Tooltip>
          );
        } else {
          return (
            <Typography variant="caption" color="text.secondary">
              None
            </Typography>
          );
        }
      },
    },
    {
      field: 'active',
      headerName: 'Status',
      width: 100,
      align: 'center',
      sortable: false,
      filterable: true,
      valueGetter: (row) => row.active ? 1 : 0,
      renderCell: (row) => (
        <Chip
          label={row.active ? 'Active' : 'Archived'}
          color={row.active ? 'success' : 'default'}
          size="small"
        />
      ),
    },
  ];

  // Define row actions
  const rowActions: TableAction<StripeProductResponse>[] = [
    {
      label: 'View Details',
      icon: <VisibilityIcon fontSize="small" />,
      onClick: (row) => handleViewDetails(row),
    },
    {
      label: 'View in Stripe Dashboard',
      icon: <OpenInNewIcon fontSize="small" />,
      onClick: (row) => window.open(`https://dashboard.stripe.com/products/${row.id}`, '_blank'),
    },
    {
      label: 'Archive Product',
      icon: <ArchiveIcon fontSize="small" />,
      onClick: (row) => handleArchive(row.id, row.name),
      visible: (row) => row.active,
      color: 'error',
    },
  ];

  return (
    <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      {error && (
        <Alert severity="error" sx={{ flexShrink: 0, mb: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <EnhancedTable
        data={filteredProducts}
        totalCount={filteredProducts.length}
        loading={loading}
        columns={columns}
        getRowId={(row) => row.id}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={[10, 25, 50, 100]}
        onPageChange={setPage}
        onPageSizeChange={(size: number) => {
          setPageSize(size);
          setPage(1);
        }}
        searchable
        searchTerm={searchTerm}
        searchPlaceholder="Search by product name, ID, or description..."
        onSearchChange={setSearchTerm}
        filters={filters}
        filterOptions={[
          {
            field: 'status',
            label: 'Status',
            type: 'select',
            options: [
              { label: 'Active', value: 'active' },
              { label: 'Archived', value: 'archived' },
            ],
          },
        ]}
        onFilterChange={setFilters}
        actions={rowActions}
        toolbarActions={[
          {
            label: 'Open Stripe Dashboard',
            icon: <OpenInNewIcon />,
            onClick: () => window.open('https://dashboard.stripe.com/products', '_blank', 'noopener,noreferrer'),
            variant: 'outlined',
          },
        ]}
        onAdd={onAdd}
        addLabel={addLabel}
        exportable
        onExport={handleExport}
        emptyStateTitle="No Stripe Products Found"
        emptyStateDescription="Create your first subscription plan to get started."
        rowHeight={56}
        stickyHeader
        maxHeight="100%"
      />

      {/* Product Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Product Details</DialogTitle>
        <DialogContent dividers>
          {selectedProduct && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Product Name
                </Typography>
                <Typography variant="body1">{selectedProduct.name}</Typography>
              </Box>

              {selectedProduct.description && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1">{selectedProduct.description}</Typography>
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Product ID
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" fontFamily="monospace">
                    {selectedProduct.id}
                  </Typography>
                  <AppIconButton
                    size="small"
                    onClick={() => handleCopyToClipboard(selectedProduct.id, 'Product ID')}
                  >
                    <CopyIcon fontSize="small" />
                  </AppIconButton>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Pricing Details
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Resource Type</TableCell>
                        <TableCell>Price ID</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Interval</TableCell>
                        <TableCell>Usage Type</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedProduct.prices.map((price: StripePrice) => (
                        <TableRow key={price.id}>
                          <TableCell>
                            <Chip label={price.metadata.resource_type || 'Unknown'} size="small" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" fontFamily="monospace">
                              {price.id}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{formatCurrency(price.unit_amount)}</TableCell>
                          <TableCell>{price.recurring?.interval || 'N/A'}</TableCell>
                          <TableCell>
                            {price.recurring?.usage_type ? (
                              <Chip label={price.recurring.usage_type} size="small" variant="outlined" />
                            ) : (
                              'N/A'
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Metadata
                </Typography>
                <JsonEditor
                  value={jsonStringify(selectedProduct.metadata)}
                  readOnly
                  minHeight={200}
                />
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <AppButton onClick={() => setDetailsOpen(false)}>Close</AppButton>
          {selectedProduct && (
            <AppButton
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={() =>
                window.open(`https://dashboard.stripe.com/products/${selectedProduct.id}`, '_blank')
              }
            >
              View in Stripe
            </AppButton>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProviderSubscriptionTable;
